import { CircleHelp } from 'lucide-react';
import { ChangeEvent, PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { TagPicker } from 'rsuite';
import z from 'zod';
import { useCreateSubtenantMutation } from '../../api';
import { useAuthentication } from '../../auth';
import { Button } from '../../components/Button';
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/Dialog';
import { NumberInput } from '../../components/Input/NumberInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/Input/Select';
import { ErrorMessage, InputLabel, TextInput } from '../../components/Input/TextInput';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../components/Tooltip';
import { useToaster } from '../../hooks/useToaster';
import { useValidator } from '../../hooks/useValidator';
import { isAdmin } from '../../util/auth';
import { timezones } from '../../util/timezones';
import { useSearchUsers } from '../hooks/useSearchUsers';

const formValuesSchema = z.object({
  label: z
    .string()
    .min(1, 'A name is required')
    .regex(/^[a-zA-Z0-9- ]+$/, 'Name can only contain letters, numbers, dashes, or spaces'),
  lifeglovLinerStockLowThreshold: z.number().int().nonnegative().nullable(),
  monthlyCapacity: z.number().int().positive().nullable(),
  timezone: z.string().min(1, 'A timezone is required'),
  adminIdsToAssign: z.array(z.string()).optional(),
  adminEmailsToInvite: z.array(z.email()).optional()
});

export function AddClinicDialog({ children }: PropsWithChildren) {
  const currentUser = useAuthentication();
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [formValues, setFormValues] = useState<z.infer<typeof formValuesSchema>>({
    label: '',
    lifeglovLinerStockLowThreshold: 3,
    monthlyCapacity: null,
    timezone: 'UTC'
  });
  const { validate, errors } = useValidator(formValuesSchema);
  const [createSubtenant] = useCreateSubtenantMutation();

  const toast = useToaster();

  const { users, isLoading: isLoadingUsers } = useSearchUsers({ searchTerm: '' });

  const adminOptions = useMemo(() => {
    const admins = users.filter(isAdmin);

    const adminsToInvite =
      formValues.adminEmailsToInvite?.map((email) => ({
        email,
        id: email,
        firstName: '',
        lastName: ''
      })) ?? [];

    return [...admins, ...adminsToInvite]
      .map((user) => {
        const fullName = `${user.firstName} ${user.lastName}`.trim();
        const label = `${user.email}${fullName ? ` (${fullName})` : ''}`;
        return { value: user.id, label };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [users, formValues.adminEmailsToInvite]);

  const name = useMemo(
    () => currentUser.tenant + '-' + formValues.label.toLowerCase().trim().replace(/\s+/g, '-'),
    [currentUser.tenant, formValues.label]
  );

  const onSubmit = useCallback(async () => {
    if (!validate(formValues)) {
      return;
    }

    const { label, monthlyCapacity, lifeglovLinerStockLowThreshold, timezone, adminEmailsToInvite } = formValues;
    const adminIdsToAssign = formValues.adminIdsToAssign?.filter((id) => !adminEmailsToInvite?.includes(id));

    setIsLoading(true);

    try {
      await createSubtenant({
        name,
        label,
        monthlyCapacity,
        lifeglovLinerStockLowThreshold,
        timezone,
        adminIdsToAssign,
        adminEmailsToInvite
      }).unwrap();
      toast.success('Clinic added successfully.');
      setOpen(false);
    } catch {
      toast.error('Failed to add clinic. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [validate, formValues, createSubtenant, name, toast]);

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        setOpen(open);
        setFormValues({
          label: '',
          lifeglovLinerStockLowThreshold: 3,
          monthlyCapacity: null,
          timezone: 'UTC'
        });
      }}
    >
      {children}

      <DialogContent
        className="shadow-2xl"
        // Necessary workaround to fix scrolling within the user picker:
        // https://github.com/radix-ui/primitives/issues/1159
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>Add Clinic</DialogTitle>
        </DialogHeader>

        <form className="flex flex-col gap-4">
          <TextInput
            label="Name*"
            onChange={(label) => setFormValues((prev) => ({ ...prev, label }))}
            errorMessage={errors.label}
          />

          <div>
            <InputLabel>Time Zone*</InputLabel>
            <Select
              value={formValues.timezone}
              onValueChange={(timezone) => setFormValues((prev) => ({ ...prev, timezone }))}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a time zone" />
              </SelectTrigger>
              <SelectContent>
                {timezones.map((tz) => (
                  <SelectItem key={tz} value={tz}>
                    {tz}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.timezone && <ErrorMessage>{errors.timezone}</ErrorMessage>}
          </div>

          <NumberInput
            label={
              <span className="flex items-center gap-1">
                <span>Low Stock Threshold</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <CircleHelp className="w-4" />
                  </TooltipTrigger>
                  <TooltipContent className="flex items-center gap-2">
                    <span>
                      An email notification will be sent to admins when the Lifeglov liner stock reaches this threshold.
                    </span>
                  </TooltipContent>
                </Tooltip>
              </span>
            }
            name="lifeglovLinerStockLowThreshold"
            value={formValues.lifeglovLinerStockLowThreshold ?? undefined}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const value = isNaN(e.target.valueAsNumber) ? null : e.target.valueAsNumber;
              setFormValues((prev) => ({ ...prev, lifeglovLinerStockLowThreshold: value }));
            }}
            errorMessage={errors.lifeglovLinerStockLowThreshold}
            step={1}
            min={0}
          />

          <NumberInput
            label={
              <span className="flex items-center gap-1">
                <span>Monthly Census</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <CircleHelp className="w-4" />
                  </TooltipTrigger>
                  <TooltipContent className="flex items-center gap-2">
                    <span>The number of patients this clinic is treating in a month.</span>
                  </TooltipContent>
                </Tooltip>
              </span>
            }
            name="monthlyCapacity"
            value={formValues.monthlyCapacity ?? undefined}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const value = isNaN(e.target.valueAsNumber) ? null : e.target.valueAsNumber;
              setFormValues((prev) => ({ ...prev, monthlyCapacity: value }));
            }}
            errorMessage={errors.monthlyCapacity}
            step={1}
            min={1}
          />

          <div>
            <InputLabel>Invite Administrators</InputLabel>
            <TagPicker
              data={adminOptions}
              value={formValues.adminIdsToAssign}
              onChange={(adminIdsToAssign) => setFormValues((prev) => ({ ...prev, adminIdsToAssign }))}
              loading={isLoadingUsers}
              creatable
              onCreate={(_, item) => {
                const email = item.value as string;
                setFormValues((prev) => ({
                  ...prev,
                  adminEmailsToInvite: [...(prev.adminEmailsToInvite ?? []), email]
                }));
              }}
              placeholder="Select existing users or invite new users by email"
              className="dark w-full"
              style={{ maxHeight: '250px' }}
              menuStyle={{ maxHeight: '250px', overflow: 'auto' }}
            />
          </div>
        </form>

        <DialogFooter>
          <Button variant="accent" onClick={onSubmit} disabled={isLoading} isLoading={isLoading}>
            Add
          </Button>

          <DialogClose asChild>
            <Button variant="transparent">Cancel</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
